import { useState, type FormEvent, type ReactNode } from "react";
import { ApiError, ApiUnreachableError } from "../api/client.js";
import { usePortal } from "../app/context.js";
import { IconScannix } from "../components/Icons.jsx";
import { RequestPasswordReset } from "./AccountLinks.jsx";

/**
 * Sign in.
 *
 * Three things here are deliberate and easy to get wrong.
 *
 * The organisation field is optional and hidden behind a disclosure, because
 * most people belong to one organisation and the API resolves it alone. Only
 * somebody who works for an umbrella and a subcontractor needs it, and putting
 * a required "organisation id" box on the front door would make the common case
 * look harder than it is.
 *
 * The reason a previous session ended is shown above the form. Without it, a
 * session that the API refused because the user belongs to more than one
 * organisation simply returns the person to this screen with no explanation,
 * which is the single most baffling thing this product could do to somebody.
 * It is also where a surveyor whose access has been withdrawn finds that out,
 * in the API's own words, rather than being returned to a blank form.
 *
 * "Forgotten your password?" swaps this form for the reset request rather than
 * navigating: the person is signed out, so there is no route to protect and no
 * back button to get wrong, and the thing they asked for appears where they
 * were already looking.
 */
export function SignIn(): ReactNode {
  const { client, session } = usePortal();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organisationId, setOrganisationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotten, setForgotten] = useState(false);

  const endedReason = session.endedReason();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await client.login(email, password);
      session.set({
        token: result.token,
        expiresAt: result.expiresAt,
        email,
        organisationId: organisationId.trim() === "" ? null : organisationId.trim(),
      });
    } catch (cause: unknown) {
      // The API's own message where it has one. A wrong password and a locked
      // account are different problems with different fixes, and only the
      // server knows which happened.
      if (cause instanceof ApiUnreachableError) {
        setError("The API did not answer. Check it is running, then try again.");
      } else if (cause instanceof ApiError) {
        setError(cause.message);
      } else {
        setError("Sign in failed.");
      }
      setBusy(false);
    }
  }

  return (
    <main className="signin">
      <div className="signin__card">
        <span className="signin__mark" aria-hidden="true">
          <IconScannix />
        </span>
        <h1 className="wordmark">Scannix</h1>
        <p className="lede">Installer portal</p>

        {endedReason ? (
          <div className="notice warn" role="status">
            <p>{endedReason}</p>
          </div>
        ) : null}

        {forgotten ? (
          <RequestPasswordReset onDone={() => setForgotten(false)} />
        ) : (
        <form onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <details>
          <summary>I work for more than one organisation</summary>
          <div className="field">
            <label htmlFor="organisationId">Organisation id</label>
            <input
              id="organisationId"
              name="organisationId"
              value={organisationId}
              onChange={(e) => setOrganisationId(e.target.value)}
            />
            <p className="hint small">
              Leave this empty unless the API has told you it cannot work out
              which organisation you are acting for.
            </p>
          </div>
        </details>

        {error ? (
          <div className="notice fail" role="alert">
            <p>{error}</p>
          </div>
        ) : null}

          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>{" "}
          <button
            type="button"
            className="link"
            onClick={() => setForgotten(true)}
          >
            Forgotten your password?
          </button>
        </form>
        )}
      </div>
    </main>
  );
}
